import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";

import MDButton from "components/MDButton";
import NegativeImage from "assets/images/NegativeSelfReporting.svg?react";
import PositiveImage from "assets/images/PossitiveSelfReporting.svg?react";
import { styles } from "./SelfReportScreen.styles";
import { ResultActionType } from "types/resultActions";
import { useAppSelector } from "store/hooks";
import { getPendingUpload } from "store/slices/imageRecognition";
import { uploadTestImages } from "services/imageUpload";
import { SelfReportedResult } from "types/models";
import { logSelfReportAnalytics } from "services/selfReportLogger";
import { sentryLogMessage } from "services/sentryLogger";
import { SENTRY_EVENT_TYPES } from "constants/sentryEvents";
import { getModelMetaDataTags } from "services/models";

interface StepProps { 
  handleUpdateStep: (step: number) => void;
  setResultAction: (action: ResultActionType) => void;
}

const SelfReportScreen: FC<StepProps> = ({ handleUpdateStep, setResultAction }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const useStyles = makeStyles()(() => styles(theme));
  const { classes } = useStyles();
  const pendingUpload = useAppSelector(getPendingUpload);
  
  const [selectedResult, setSelectedResult] = useState<SelfReportedResult | null>(null);

  const handleSubmitResult = async (selfReportedResult: SelfReportedResult) => {
    const submissionTimestamp = Date.now();

    // Navigate to end modal immediately (non-blocking UI)
    handleUpdateStep(6);
    setResultAction("DONE");
    // Log completed test event when updating to step 6
    sentryLogMessage({
      message: 'Test completed',
      level: 'info',
      tags: {
        component: 'SelfReportScreen',
        category: 'test_completed',
        event_type: SENTRY_EVENT_TYPES.COMPLETED_TEST,
        ...getModelMetaDataTags(),
      },
      extra: {
        timestamp: new Date().toISOString(),
      },
    });

    // Log result submitted event
    sentryLogMessage({
      message: 'Result submitted by user',
      level: 'info',
      tags: {
        component: 'SelfReportScreen',
        category: 'result_submitted',
        event_type: SENTRY_EVENT_TYPES.RESULT_SUBMITTED,
        ...getModelMetaDataTags(),
      },
      extra: {
        selfReportedResult,
        modelResult: pendingUpload?.modelResult || 'INVALID',
        timestamp: new Date().toISOString(),
      },
    });

    // Trigger image upload in background (non-blocking)
    if (pendingUpload) {
      try {
        const uploadResult = await uploadTestImages(
          pendingUpload.images,
          pendingUpload.modelResult,
          pendingUpload.metadata,
          pendingUpload.testType,
          pendingUpload.sessionId,
          selfReportedResult,
          pendingUpload.maxQualityImageIndex
        );
        logSelfReportAnalytics({
          timestamp: submissionTimestamp,
          selfReportedResult,
          modelResult: pendingUpload.modelResult,
          imageDownloadUrls: uploadResult?.imageDownloadUrls,
        }).catch((analyticsError) => {
          console.error('Failed to log analytics:', analyticsError);
        });
      } catch (error) {
        console.error('Failed to upload test images:', error);
      }
     
    } else {
      console.warn("SelfReportScreen: No pending upload data found");

      // Log to analytics logger even without pending upload data (non-blocking)
      logSelfReportAnalytics({
        timestamp: submissionTimestamp,
        selfReportedResult,
        modelResult: "INVALID",
        imageDownloadUrls: undefined,
      }).catch((error) => {
        console.error('Failed to log analytics:', error);
      });
    }
  };

  const handleCardClick = (result: SelfReportedResult) => {
    setSelectedResult(result);
  };

  return (
    <div className={classes.container}>
      <div className={classes.helperText}>
        {t("SelfReportScreen:helperText")}
      </div>
      
      <div className={classes.optionsContainer}>
        <div
          className={`${classes.optionCard} ${
            selectedResult === 'negative' ? classes.optionCardSelected : ""
          }`}
          onClick={() => handleCardClick('negative')}
        >
          <div className={classes.optionLinesText}>
            {t("SelfReportScreen:oneLine")}
          </div>
          <div className={classes.optionImage}>
            <NegativeImage />
          </div>
          <div className={classes.optionLabel}>
            {t("SelfReportScreen:negative")}
          </div>
        </div>
        <div
          className={`${classes.optionCard} ${
            selectedResult === 'positive' ? classes.optionCardSelected : ""
          }`}
          onClick={() => handleCardClick('positive')}
        >
          <div className={classes.optionLinesText}>
            {t("SelfReportScreen:twoLines")}
          </div>
          <div className={classes.optionImage}>
            <PositiveImage />
          </div>
          <div className={classes.optionLabel}>
            {t("SelfReportScreen:positive")}
          </div>
        </div>

      </div>

      <div className={classes.buttonsSection}>
        <div
          role="button"
          tabIndex={0}
          className={classes.link}
          onClick={() => {
            setResultAction("SELF_REPORTING_ISSUE");
            handleUpdateStep(6);
          }}
        >
          {t("SelfReportScreen:reportIssue")}
        </div>
        <MDButton
          style={{ width: theme.width.full, height: theme.spacing.spacing28 }}
          color={"primary"}
          variant="contained"
          type="submit"
          disabled={selectedResult === null}
          onClick={() => {
            if (selectedResult !== null) {
              handleSubmitResult(selectedResult);
            }
          }}
        >
          {t("SelfReportScreen:submitButton")}
        </MDButton>
      </div>
    </div>
  );
};

export default SelfReportScreen;

